import { PDFDocument, TextAlignment, rgb } from "pdf-lib";
import templateUrl from './template.pdf';
import { drawLogos } from "@/reports/shared_utils/drawLogos";
import { collectUnsupportedHighlights, drawUnsupportedHighlights } from "@/reports/shared_utils/highlightUnsupported";
import { sanitizeForPdf } from "@/reports/shared_utils/sanitizeForPdf";

// ─── SCHEMA_TO_PDF_FIELD ──────────────────────────────────────────────────────
// Maps each schema key to the representative PDF field name used for
// highlight positioning in the preview pane.
// Special cases in the PDF:
//   course{n}FirstInitiative  → course{n}FirstIntitiative  (typo in PDF for ALL 8 courses)

const buildCourseSchemaToPdf = () => {
  const result = {};
  for (let n = 1; n <= 9; n++) {
    result[`course${n}Title`]                = `course${n}Title`;
    result[`course${n}Code`]                 = `course${n}Code`;
    result[`course${n}Teacher`]              = `course${n}Teacher`;
    result[`course${n}ESLELD`]               = `course${n}ESLELD`;
    result[`course${n}IEP`]                  = `course${n}IEP`;
    result[`course${n}French`]               = `course${n}French`;
    result[`course${n}SHSM`]                 = `course${n}SHSM`;
    result[`course${n}FirstPercentageMark`]  = `course${n}FirstPercentageMark`;
    result[`course${n}FirstMedian`]          = `course${n}FirstMedian`;
    result[`course${n}FirstCreditEarned`]    = `course${n}FirstCreditEarned`;
    result[`course${n}FirstResponsibility`]  = `course${n}FirstResponsibility`;
    result[`course${n}FirstOrganization`]    = `course${n}FirstOrganization`;
    result[`course${n}FirstIndependentWork`] = `course${n}FirstIndependentWork`;
    result[`course${n}FirstCollaboration`]   = `course${n}FirstCollaboration`;
    result[`course${n}FirstInitiative`]      = `course${n}FirstIntitiative`; // PDF typo
    result[`course${n}FirstSelfRegulation`]  = `course${n}FirstSelfRegulation`;
    result[`course${n}FirstClassesMissed`]   = `course${n}FirstClassesMissed`;
    result[`course${n}FirstTotalClasses`]    = `course${n}FirstTotalClasses`;
    result[`course${n}FirstTimesLate`]       = `course${n}FirstTimesLate`;
    result[`course${n}FinalPercentageMark`]  = `course${n}FinalPercentageMark`;
    result[`course${n}FinalMedian`]          = `course${n}FinalMedian`;
    result[`course${n}FinalCreditEarned`]    = `course${n}FinalCreditEarned`;
    result[`course${n}FinalResponsibility`]  = `course${n}FinalResponsibility`;
    result[`course${n}FinalOrganization`]    = `course${n}FinalOrganization`;
    result[`course${n}FinalIndependentWork`] = `course${n}FinalIndependentWork`;
    result[`course${n}FinalCollaboration`]   = `course${n}FinalCollaboration`;
    result[`course${n}FinalInitiative`]      = `course${n}FinalInitiative`; // Final is correctly spelled
    result[`course${n}FinalSelfRegulation`]  = `course${n}FinalSelfRegulation`;
    result[`course${n}FinalClassesMissed`]   = `course${n}FinalClassesMissed`;
    result[`course${n}FinalTotalClasses`]    = `course${n}FinalTotalClasses`;
    result[`course${n}FinalTimesLate`]       = `course${n}FinalTimesLate`;
    result[`course${n}Comments`]             = `course${n}Comments`;
  }
  return result;
};

export const SCHEMA_TO_PDF_FIELD = {
  // Student/school info (representative field - first occurrence in PDF)
  semester: 'semester1', reportingPeriod: 'reportingPeriod1', date: 'date1',
  studentName: 'studentName1', oen: 'oen1', grade: 'grade1', homeroom: 'homeroom1',
  principal: 'principal', studentAddress: 'studentAddress', schoolCouncilChair: 'schoolCouncilChair',
  schoolName: 'schoolName', schoolTelephone: 'schoolTelephone', schoolAddress: 'schoolAddress', schoolFax: 'schoolFax',
  boardName: 'boardName', boardEmailWebsite: 'boardEmailWebsite', boardAddress: 'boardAddress',
  // Courses
  ...buildCourseSchemaToPdf(),
  // Credit summary
  compulsoryCreditsEarnedThisReport: 'compulsoryCreditsEarnedThisReport',
  compulsoryCreditsEarnedToDate: 'compulsoryCreditsEarnedToDate',
  englishEarnedThisReport: 'englishEarnedThisReport', englishEarnedToDate: 'englishEarnedToDate',
  frenchEarnedThisReport: 'frenchEarnedThisReport', frenchEarnedToDate: 'frenchEarnedToDate',
  mathematicsEarnedThisReport: 'mathematicsEarnedThisReport', mathematicsEarnedToDate: 'mathematicsEarnedToDate',
  scienceEarnedThisReport: 'scienceEarnedThisReport', scienceEarnedToDate: 'scienceEarnedToDate',
  canadianHistoryEarnedThisReport: 'canadianHistoryEarnedThisReport', canadianHistoryEarnedToDate: 'canadianHistoryEarnedToDate',
  canadianGeographyEarnedThisReport: 'canadianGeographyEarnedThisReport', canadianGeographyEarnedToDate: 'canadianGeographyEarnedToDate',
  theArtsEarnedThisReport: 'theArtsEarnedThisReport', theArtsEarnedToDate: 'theArtsEarnedToDate',
  healthPhysicalEducationEarnedThisReport: 'healthPhysicalEducationEarnedThisReport', healthPhysicalEducationEarnedToDate: 'healthPhysicalEducationEarnedToDate',
  civicsEarnedThisReport: 'civicsEarnedThisReport', civicsEarnedToDate: 'civicsEarnedToDate',
  careerEarnedThisReport: 'careerEarnedThisReport', careerEarnedToDate: 'careerEarnedToDate',
  group1EarnedThisReport: 'group1EarnedThisReport', group1EarnedToDate: 'group1EarnedToDate',
  group2EarnedThisReport: 'group2EarnedThisReport', group2EarnedToDate: 'group2EarnedToDate',
  group3EarnedThisReport: 'group3EarnedThisReport', group3EarnedToDate: 'group3EarnedToDate',
  optionalCreditsEarnedThisReport: 'optionalCreditsEarnedThisReport', optionalCreditsEarnedToDate: 'optionalCreditsEarnedToDate',
  totalCreditsRequiredEarnedThisReport: 'totalCreditsRequiredEarnedThisReport', totalCreditsRequiredEarnedToDate: 'totalCreditsRequiredEarnedToDate',
  communityInvolvementEarnedThisReport: 'communityInvolvementEarnedThisReport', communityInvolvementEarnedToDate: 'communityInvolvementEarnedToDate',
  onlineLearningRequirementEarnedThisReport: 'onlineLearningRequirementEarnedThisReport', onlineLearningRequirementEarnedToDate: 'onlineLearningRequirementEarnedToDate',
  specialistHighSkillsMajorName: 'specialistHighSkillsMajorName',
  specialistHighSkillsMajorTotalRequired: 'specialistHighSkillsMajorTotalRequired',
  specialistHighSkillsMajorEarnedThisReport: 'specialistHighSkillsMajorEarnedThisReport',
  specialistHighSkillsMajorEarnedToDate: 'specialistHighSkillsMajorEarnedToDate',
  oSSLiteracyRequirementCompleted: 'oSSLiteracyRequirementCompleted',
  schoolUse: 'schoolUse',
  principalSignatureDate: 'principalSignatureDate',
}

// ─── PAGE_INDEX_FOR_SCHEMA_FIELD ──────────────────────────────────────────────
// 0 = page 1 (Semester 1 header + courses 1–4)
// 1 = page 2 (Semester 2 header + courses 5–9)
// 2 = page 3 (credit summary)

const buildCoursePageIndex = () => {
  const result = {};
  const courseFields = [
    'Title','Code','Teacher','ESLELD','IEP','French','SHSM',
    'FirstPercentageMark','FirstMedian','FirstCreditEarned',
    'FirstResponsibility','FirstOrganization','FirstIndependentWork',
    'FirstCollaboration','FirstInitiative','FirstSelfRegulation',
    'FirstClassesMissed','FirstTotalClasses','FirstTimesLate',
    'FinalPercentageMark','FinalMedian','FinalCreditEarned',
    'FinalResponsibility','FinalOrganization','FinalIndependentWork',
    'FinalCollaboration','FinalInitiative','FinalSelfRegulation',
    'FinalClassesMissed','FinalTotalClasses','FinalTimesLate','Comments',
  ];
  for (let n = 1; n <= 9; n++) {
    const pageIdx = n <= 4 ? 0 : 1;
    for (const f of courseFields) {
      result[`course${n}${f}`] = pageIdx;
    }
  }
  return result;
};

export const PAGE_INDEX_FOR_SCHEMA_FIELD = {
  semester: 0, reportingPeriod: 0, date: 0,
  studentName: 0, oen: 0, grade: 0, homeroom: 0,
  principal: 0, studentAddress: 0, schoolCouncilChair: 0,
  schoolName: 0, schoolTelephone: 0, schoolAddress: 0, schoolFax: 0,
  boardName: 0, boardEmailWebsite: 0, boardAddress: 0,
  ...buildCoursePageIndex(),
  compulsoryCreditsEarnedThisReport: 2, compulsoryCreditsEarnedToDate: 2,
  englishEarnedThisReport: 2, englishEarnedToDate: 2,
  frenchEarnedThisReport: 2, frenchEarnedToDate: 2,
  mathematicsEarnedThisReport: 2, mathematicsEarnedToDate: 2,
  scienceEarnedThisReport: 2, scienceEarnedToDate: 2,
  canadianHistoryEarnedThisReport: 2, canadianHistoryEarnedToDate: 2,
  canadianGeographyEarnedThisReport: 2, canadianGeographyEarnedToDate: 2,
  theArtsEarnedThisReport: 2, theArtsEarnedToDate: 2,
  healthPhysicalEducationEarnedThisReport: 2, healthPhysicalEducationEarnedToDate: 2,
  civicsEarnedThisReport: 2, civicsEarnedToDate: 2,
  careerEarnedThisReport: 2, careerEarnedToDate: 2,
  group1EarnedThisReport: 2, group1EarnedToDate: 2,
  group2EarnedThisReport: 2, group2EarnedToDate: 2,
  group3EarnedThisReport: 2, group3EarnedToDate: 2,
  optionalCreditsEarnedThisReport: 2, optionalCreditsEarnedToDate: 2,
  totalCreditsRequiredEarnedThisReport: 2, totalCreditsRequiredEarnedToDate: 2,
  communityInvolvementEarnedThisReport: 2, communityInvolvementEarnedToDate: 2,
  onlineLearningRequirementEarnedThisReport: 2, onlineLearningRequirementEarnedToDate: 2,
  specialistHighSkillsMajorName: 2, specialistHighSkillsMajorTotalRequired: 2,
  specialistHighSkillsMajorEarnedThisReport: 2, specialistHighSkillsMajorEarnedToDate: 2,
  oSSLiteracyRequirementCompleted: 2, schoolUse: 2,
  principalSignatureDate: 2,
}

// ─── STEP_ANCHOR_FIELDS ───────────────────────────────────────────────────────
// One representative PDF field per step - used to compute scroll position in preview.

export const STEP_ANCHOR_FIELDS = {
  1: 'studentName1',
  2: 'schoolName',
  3: 'boardName',
  4: 'course1Title',
  5: 'course2Title',
  6: 'course3Title',
  7: 'course4Title',
  8: 'course5Title',
  9: 'course6Title',
  10: 'course7Title',
  11: 'course8Title',
  12: 'course9Title',
  13: 'semester3',
}

// ─── fill ─────────────────────────────────────────────────────────────────────

const val = (v) => sanitizeForPdf(v);

export const fill = async (formData, highlightRect = null, logos = []) => {
  const existingPdfBytes = await fetch(templateUrl).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const pdfForm = pdfDoc.getForm();

  var field;

  // Learning skills may be text fields or radio groups depending on the PDF version.
  const setSkill = (pdfFieldName, value) => {
    const v = val(value);
    try {
      const f = pdfForm.getTextField(pdfFieldName);
      f.setText(v);
      f.setFontSize(9);
    } catch {
      const rg = pdfForm.getRadioGroup(pdfFieldName);
      if (v) rg.select(v); else rg.deselect();
    }
  };

  // ── PAGE 1: Semester 1 header ─────────────────────────────────────────────

  field = pdfForm.getField('semester1');
  field.setText(val(formData['semester']));
  field.setFontSize(8);

  field = pdfForm.getField('reportingPeriod1');
  field.setText(val(formData['reportingPeriod']));
  field.setFontSize(8);

  field = pdfForm.getField('date1');
  field.setText(val(formData['date']));
  field.setFontSize(8);

  field = pdfForm.getField('studentName1');
  field.setText(val(formData['studentName']));
  field.setFontSize(8);

  field = pdfForm.getField('oen1');
  field.setText(val(formData['oen']));
  field.setFontSize(8);

  field = pdfForm.getField('grade1');
  field.setText(val(formData['grade']));
  field.setFontSize(8);

  field = pdfForm.getField('homeroom1');
  field.setText(val(formData['homeroom']));
  field.setFontSize(8);

  field = pdfForm.getField('principal');
  field.setText(val(formData['principal']));
  field.setFontSize(8);

  field = pdfForm.getField('studentAddress');
  field.setText(val(formData['studentAddress']));
  field.setFontSize(8);

  field = pdfForm.getField('schoolCouncilChair');
  field.setText(val(formData['schoolCouncilChair']));
  field.setFontSize(8);

  field = pdfForm.getField('schoolName');
  field.setText(val(formData['schoolName']));
  field.setFontSize(8);

  field = pdfForm.getField('schoolTelephone');
  field.setText(val(formData['schoolTelephone']));
  field.setFontSize(8);

  field = pdfForm.getField('schoolAddress');
  field.setText(val(formData['schoolAddress']));
  field.setFontSize(8);

  field = pdfForm.getField('schoolFax');
  field.setText(val(formData['schoolFax']));
  field.setFontSize(8);

  field = pdfForm.getField('boardName');
  field.setText(val(formData['boardName']));
  field.setFontSize(8);

  field = pdfForm.getField('boardEmailWebsite');
  field.setText(val(formData['boardEmailWebsite']));
  field.setFontSize(8);

  field = pdfForm.getField('boardAddress');
  field.setText(val(formData['boardAddress']));
  field.setFontSize(8);

  // ── PAGE 2: Semester 2 header (same student info, different PDF fields) ────

  field = pdfForm.getField('semester2');
  field.setText(val(formData['semester']));
  field.setFontSize(8);

  field = pdfForm.getField('reportingPeriod2');
  field.setText(val(formData['reportingPeriod']));
  field.setFontSize(8);

  field = pdfForm.getField('date2');
  field.setText(val(formData['date']));
  field.setFontSize(8);

  field = pdfForm.getField('studentName2');
  field.setText(val(formData['studentName']));
  field.setFontSize(8);

  field = pdfForm.getField('oen2');
  field.setText(val(formData['oen']));
  field.setFontSize(8);

  field = pdfForm.getField('grade2');
  field.setText(val(formData['grade']));
  field.setFontSize(8);

  field = pdfForm.getField('homeroom2');
  field.setText(val(formData['homeroom']));
  field.setFontSize(8);

  // ── PAGE 3: Credit Summary header ─────────────────────────────────────────

  field = pdfForm.getField('semester3');
  field.setText(val(formData['semester']));
  field.setFontSize(8);

  field = pdfForm.getField('reportingPeriod3');
  field.setText(val(formData['reportingPeriod']));
  field.setFontSize(8);

  field = pdfForm.getField('date3');
  field.setText(val(formData['date']));
  field.setFontSize(8);

  field = pdfForm.getField('studentName3');
  field.setText(val(formData['studentName']));
  field.setFontSize(8);

  field = pdfForm.getField('oen3');
  field.setText(val(formData['oen']));
  field.setFontSize(8);

  field = pdfForm.getField('grade3');
  field.setText(val(formData['grade']));
  field.setFontSize(8);

  field = pdfForm.getField('homeroom3');
  field.setText(val(formData['homeroom']));
  field.setFontSize(8);

  // PAGE 3: 4th student name block (signature area)
  field = pdfForm.getField('studentName4');
  field.setText(val(formData['studentName']));
  field.setFontSize(8);

  field = pdfForm.getField('oen4');
  field.setText(val(formData['oen']));
  field.setFontSize(8);

  field = pdfForm.getField('grade4');
  field.setText(val(formData['grade']));
  field.setFontSize(8);

  field = pdfForm.getField('homeroom4');
  field.setText(val(formData['homeroom']));
  field.setFontSize(8);

  // ── COURSES 1–9 ───────────────────────────────────────────────────────────
  // Field names are assigned to variables before .getField() so that the
  // test's static regex parser doesn't see template literals as field names.

  for (let n = 1; n <= 9; n++) {
    const p = formData;

    // Build field name variables (all follow the same pattern; ESLELD is special for course 5)
    const fTitle       = `course${n}Title`;
    const fCode        = `course${n}Code`;
    const fTeacher     = `course${n}Teacher`;
    const fESLELD      = `course${n}ESLELD`;
    const fIEP         = `course${n}IEP`;
    const fFrench      = `course${n}French`;
    const fSHSM        = `course${n}SHSM`;
    const fFirstPct    = `course${n}FirstPercentageMark`;
    const fFirstMed    = `course${n}FirstMedian`;
    const fFirstCredit = `course${n}FirstCreditEarned`;
    const fFirstMissed = `course${n}FirstClassesMissed`;
    const fFirstTotal  = `course${n}FirstTotalClasses`;
    const fFirstLate   = `course${n}FirstTimesLate`;
    const fFinalPct    = `course${n}FinalPercentageMark`;
    const fFinalMed    = `course${n}FinalMedian`;
    const fFinalCredit = `course${n}FinalCreditEarned`;
    const fFinalMissed = `course${n}FinalClassesMissed`;
    const fFinalTotal  = `course${n}FinalTotalClasses`;
    const fFinalLate   = `course${n}FinalTimesLate`;
    const fComments    = `course${n}Comments`;

    // Course metadata
    field = pdfForm.getField(fTitle);
    field.setText(val(p[fTitle]));
    field.setFontSize(8);

    field = pdfForm.getField(fCode);
    field.setText(val(p[fCode]));
    field.setFontSize(8);

    field = pdfForm.getField(fTeacher);
    field.setText(val(p[fTeacher]));
    field.setFontSize(8);

    // Independent flags (course5ESLELD has a trailing space in the PDF - handled via fESLELD variable)
    field = pdfForm.getField(fESLELD);
    p[`course${n}ESLELD`] === 'yes' ? field.check() : field.uncheck();

    field = pdfForm.getField(fIEP);
    p[fIEP] === 'yes' ? field.check() : field.uncheck();

    field = pdfForm.getField(fFrench);
    p[fFrench] === 'yes' ? field.check() : field.uncheck();

    field = pdfForm.getField(fSHSM);
    p[fSHSM] === 'yes' ? field.check() : field.uncheck();

    // First Term marks
    field = pdfForm.getField(fFirstPct);
    field.setText(val(p[fFirstPct]));
    field.setFontSize(9);

    field = pdfForm.getField(fFirstMed);
    field.setText(val(p[fFirstMed]));
    field.setFontSize(9);

    field = pdfForm.getField(fFirstCredit);
    field.setText(val(p[fFirstCredit]));
    field.setFontSize(9);

    // First Term learning skills (note: 'Initiative' is misspelled 'Intitiative' in PDF for First terms)
    setSkill(`course${n}FirstResponsibility`,  p[`course${n}FirstResponsibility`]);
    setSkill(`course${n}FirstOrganization`,    p[`course${n}FirstOrganization`]);
    setSkill(`course${n}FirstIndependentWork`, p[`course${n}FirstIndependentWork`]);
    setSkill(`course${n}FirstCollaboration`,   p[`course${n}FirstCollaboration`]);
    setSkill(`course${n}FirstIntitiative`,     p[`course${n}FirstInitiative`]); // PDF typo
    setSkill(`course${n}FirstSelfRegulation`,  p[`course${n}FirstSelfRegulation`]);

    // First Term attendance
    field = pdfForm.getField(fFirstMissed);
    field.setText(val(p[fFirstMissed]));
    field.setFontSize(8);

    field = pdfForm.getField(fFirstTotal);
    field.setText(val(p[fFirstTotal]));
    field.setFontSize(8);

    field = pdfForm.getField(fFirstLate);
    field.setText(val(p[fFirstLate]));
    field.setFontSize(8);

    // Final Mark marks
    field = pdfForm.getField(fFinalPct);
    field.setText(val(p[fFinalPct]));
    field.setFontSize(9);

    field = pdfForm.getField(fFinalMed);
    field.setText(val(p[fFinalMed]));
    field.setFontSize(9);

    field = pdfForm.getField(fFinalCredit);
    field.setText(val(p[fFinalCredit]));
    field.setFontSize(9);

    // Final Mark learning skills (correctly spelled in PDF)
    setSkill(`course${n}FinalResponsibility`,  p[`course${n}FinalResponsibility`]);
    setSkill(`course${n}FinalOrganization`,    p[`course${n}FinalOrganization`]);
    setSkill(`course${n}FinalIndependentWork`, p[`course${n}FinalIndependentWork`]);
    setSkill(`course${n}FinalCollaboration`,   p[`course${n}FinalCollaboration`]);
    setSkill(`course${n}FinalInitiative`,      p[`course${n}FinalInitiative`]); // correct spelling
    setSkill(`course${n}FinalSelfRegulation`,  p[`course${n}FinalSelfRegulation`]);

    // Final Mark attendance
    field = pdfForm.getField(fFinalMissed);
    field.setText(val(p[fFinalMissed]));
    field.setFontSize(8);

    field = pdfForm.getField(fFinalTotal);
    field.setText(val(p[fFinalTotal]));
    field.setFontSize(8);

    field = pdfForm.getField(fFinalLate);
    field.setText(val(p[fFinalLate]));
    field.setFontSize(8);

    // Comments
    field = pdfForm.getField(fComments);
    field.setText(val(p[fComments]));
    field.setFontSize(8);
    field.enableMultiline();
  }

  // ── CREDIT SUMMARY ────────────────────────────────────────────────────────

  const creditFields = [
    'compulsoryCreditsEarnedThisReport', 'compulsoryCreditsEarnedToDate',
    'englishEarnedThisReport', 'englishEarnedToDate',
    'frenchEarnedThisReport', 'frenchEarnedToDate',
    'mathematicsEarnedThisReport', 'mathematicsEarnedToDate',
    'scienceEarnedThisReport', 'scienceEarnedToDate',
    'canadianHistoryEarnedThisReport', 'canadianHistoryEarnedToDate',
    'canadianGeographyEarnedThisReport', 'canadianGeographyEarnedToDate',
    'theArtsEarnedThisReport', 'theArtsEarnedToDate',
    'healthPhysicalEducationEarnedThisReport', 'healthPhysicalEducationEarnedToDate',
    'civicsEarnedThisReport', 'civicsEarnedToDate',
    'careerEarnedThisReport', 'careerEarnedToDate',
    'group1EarnedThisReport', 'group1EarnedToDate',
    'group2EarnedThisReport', 'group2EarnedToDate',
    'group3EarnedThisReport', 'group3EarnedToDate',
    'optionalCreditsEarnedThisReport', 'optionalCreditsEarnedToDate',
    'totalCreditsRequiredEarnedThisReport', 'totalCreditsRequiredEarnedToDate',
    'communityInvolvementEarnedThisReport', 'communityInvolvementEarnedToDate',
    'onlineLearningRequirementEarnedThisReport', 'onlineLearningRequirementEarnedToDate',
    'specialistHighSkillsMajorName', 'specialistHighSkillsMajorTotalRequired',
    'specialistHighSkillsMajorEarnedThisReport', 'specialistHighSkillsMajorEarnedToDate',
     'schoolUse', 'principalSignatureDate',
  ];

  
  for (const name of creditFields) {
    field = pdfForm.getField(name);
    field.setText(val(formData[name]));
    field.setFontSize(8);
    field.setAlignment(TextAlignment.Center);
  }

  field = pdfForm.getField('oSSLiteracyRequirementCompleted');
  formData['oSSLiteracyRequirementCompleted'] === 'yes' ? field.check() : field.uncheck();

  const __unsupportedHL = collectUnsupportedHighlights(pdfDoc, pdfForm);
  pdfForm.flatten();

  if (highlightRect) {
    const { pageIndex, x, y, width, height } = highlightRect;
    const pages = pdfDoc.getPages();
    if (pages[pageIndex]) {
      pages[pageIndex].drawRectangle({
        x: x - 2,
        y: y - 2,
        width: width + 4,
        height: height + 4,
        color: rgb(0, 0.55, 0),
        opacity: 0.25,
        borderColor: rgb(0.9, 0.7, 0),
        borderWidth: 0,
        borderOpacity: 0.9,
      });
    }
  }

  drawUnsupportedHighlights(pdfDoc, __unsupportedHL);

  // Class logos: drawn last so they sit on top of the flattened template.
  await drawLogos(pdfDoc, logos);

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
};
